<template>
    <div ref="portfolio" class="h-full w-full lg:px-20 md:px-10 sm:px-2 bg-[#F1F6FA]">
        <div v-if="tradesFound" class="mx-auto container lg:p-4 gap-4 flex-col lg:flex-row">
            <div class="w-full h-full flex flex-col gap-4 overflow-x-visible">
                <!-- Shorts Tutorial Message -->
                <TutorialBanner :title="$t('helpTour_portfolio_title')" :show-until="'2025-10-01'" @start-tutorial="startHelpTour" />

                <div class="flex-1 sm:border-b border-gray-300 mb-4">
                    <ul class="flex flex-wrap justify-between items-center -mb-px">
                        <div class="flex flex-wrap font-semibold">
                            <li
                                v-for="(item, index) in productGroupsSelection"
                                :key="index"
                                class="w-full sm:w-auto transition duration-400 rounded-full sm:rounded-none text-xs sm:text-sm my-1 sm:my-0 gap-6 cursor-pointer"
                                :class="{
                                    'inline-block sm:p-4 p-1 px-2 sm:bg-transparent border-b-2  bg-blue  text-white sm:text-black border-blue  active': activeTabIndex === index,
                                    'inline-block sm:p-4 p-1 px-2 sm:bg-transparent border-b-2  sm:mx-0 bg-dashboard-primary border-transparent  hover:text-black sm:hover:border-gray-300  sm:hover:scale-100  hover:shadow-lg sm:hover:shadow-none duration-300 mx-1 hover:scale-105 text-dashboard-lightGray dark:hover:text-gray-300':
                                        activeTabIndex !== index
                                }"
                                @click="(changeViewSelection(index), setAllTradesTo(true))"
                            >
                                <div class="text-center flex align-middle self-center items-center gap-1 justify-center">
                                    <p v-if="item.name === 'Neueste Investments'">{{ $t('newest_trades') }}</p>
                                    <p v-else>{{ $translate(item.name) }}</p>

                                    <info-text v-if="item.name === 'Neueste Investments'" :title="$t('newest_trades')" :text="$t('newest_trades_info')" />
                                </div>
                            </li>
                        </div>
                    </ul>
                </div>
                <!-- Übersicht -->

                <MetricsCards :card-objects="cardObjects" :title="$t('nav_long_term_trades')" :description="$t('nav_portfolio_description')" class="relative bg-white px-8 py-6">
                    <div class="absolute top-6 right-8">
                        <info-text :title="$t('nav_long_term_trades')" :text="$t('portfolio_invest_info')" />
                    </div>
                </MetricsCards>

                <!-- Charts -->
                <Loading :loading="loading">
                    <div class="bg-white rounded-xl p-4">
                        <div class="flex gap-2">
                            <select v-model="timelineIndex" class="p-2 rounded-xl border border-gray-300" @change="(clearExpandedStates(), updatePieChart())">
                                <template v-for="(timeline, index) in chartTimelineStates" :key="timeline">
                                    <option :value="index">{{ $t(timeline.title) }}</option></template
                                >
                            </select>

                            <select
                                ref="tradeFilterChartOpenClosed"
                                v-model="filterDirectionTrades"
                                :disabled="activeTabIndex === productGroupsSelection.length - 1"
                                class="p-2 rounded-xl border border-gray-300 disabled:opacity-50"
                                @change="(clearExpandedStates(), updatePieChart())"
                            >
                                <template v-for="direction in filterDirection" :key="direction">
                                    <option :value="direction">{{ direction }}</option></template
                                >
                            </select>
                        </div>
                        <div class="flex h-200 sm:min-h-500 xl:min-h-96 p-6 flex-1 flex-col xl:flex-row items-center overflow-x-scroll">
                            <div id="pieChart" class="w-full xl:w-[37.5%] mb-4 md:mb-0"></div>

                            <loading :loading="chartloading" class="w-full xl:w-[62%]">
                                <div id="graphChart"></div>
                            </loading>
                        </div>
                    </div>
                </Loading>
                <!-- Trades Table -->

                <div class="min-h-full rounded-xl bg-white p-4 sm:p-8">
                    <span class="font-bold text-base">{{ $t('plain_trades') }}</span>

                    <div class="contents flex-col text-sm font-bold text-center">
                        <!--search-->
                        <div class="flex flex-col sm:flex-row sm:items-center flex-wrap gap-4 mt-6 mb-10 z-[15] text-dashboard">
                            <!-- Suchfeld -->
                            <div class="w-full sm:w-auto flex-1">
                                <div class="group flex items-center">
                                    <div class="flex items-center w-full bg-dashboard-primary font-semibold rounded-xl overflow-hidden shadow-sm hover:shadow-md">
                                        <input
                                            v-model="searchQuery"
                                            type="search"
                                            name="zielzonen"
                                            :placeholder="$t('plain_search')"
                                            :input-use="'search'"
                                            class="block w-full px-3 py-2 border rounded-lg bg-dashboard-primary focus:outline-none text-xs"
                                        />
                                        <i class="fi fi-rr-search px-3 py-2 text-xxs border-l border-gray-300 rounded-r duration-300 transition"></i>
                                    </div>
                                </div>
                            </div>

                            <!-- Long/Short Selection Table -->
                            <select
                                ref="longShortFilter"
                                v-model="filterDirectionTrades"
                                :disabled="activeTabIndex === productGroupsSelection.length - 1"
                                class="p-2 rounded-xl bg-dashboard-primary disabled:opacity-50 font-normal focus:outline-none"
                                @change="(clearExpandedStates(), updatePieChart())"
                            >
                                <template v-for="direction in filterDirection" :key="direction">
                                    <option :value="direction">{{ direction }}</option>
                                </template>
                            </select>

                            <div class="w-full sm:w-auto">
                                <select
                                    ref="tradeFilterOpenClosed"
                                    v-model="filterTrades"
                                    class="p-2 rounded-xl bg-dashboard-primary disabled:opacity-50 font-normal focus:outline-none"
                                    @change="
                                        () => {
                                            clearExpandedStates();
                                            updatePieChart();
                                        }
                                    "
                                >
                                    <option v-for="option in tradeFilterOptions" :key="option.value" :value="option.value">
                                        {{ $t(option.label) }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <!--Table-->

                    <div class="z-0">
                        <div ref="tradeTable" class="min-h-12 rounded-xl h-[500px] overflow-x-visible z-0" :class="loading ? 'overflow-y-hidden' : 'overflow-y-scroll'">
                            <Loading :loading="loading" class="justify-between h-full">
                                <!-- Desktop & Tablet Screensize -->
                                <table class="hidden xl:table table-auto text-left text-xxs text-dashboard-lightGray w-full relative">
                                    <thead
                                        class="sticky top-0 bottom-0 text-black my-10 z-[1]"
                                        :class="{ invisible: loading }"
                                        :style="{
                                            background: 'linear-gradient(to bottom, rgba(255,255,255,1) 50%, rgba(255,255,255,0) 100%)'
                                        }"
                                    >
                                        <tr>
                                            <th class="pb-10 pl-4 z-[1] uppercase">
                                                {{ $t('plain_values') }}
                                            </th>
                                            <th class="pb-10 text-center flex flex-span gap-1 justify-center uppercase">
                                                {{ $t('plain_volume') }}
                                            </th>
                                            <th class="pb-10 text-center uppercase">
                                                {{ $t('plain_entry') }}
                                            </th>
                                            <th class="pb-10 text-center uppercase">
                                                {{ $t('plain_stop') }}
                                            </th>
                                            <th v-show="filterTrades === 'finished' || filterTrades === 'all'" class="pb-10 text-center uppercase">
                                                {{ $t('plain_exit') }}
                                            </th>
                                            <th class="pb-10 text-center uppercase">
                                                {{ $t('plain_price') }}
                                            </th>
                                            <th class="pb-10 text-center uppercase">{{ $t('price_gain') }}</th>
                                            <th class="pb-10 text-center px-2">
                                                <input
                                                    class="transition-transform duration-300"
                                                    type="checkbox"
                                                    :checked="areAllTradesSelected"
                                                    @click="((areAllTradesSelected = !areAllTradesSelected), setAllTradesTo(areAllTradesSelected), updatePieChart())"
                                                />
                                            </th>
                                            <th class="pb-10 text-center uppercase">
                                                {{ $t('plain_analysis') }}
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <!-- Neuste Trades -->
                                        <tr
                                            v-for="(row, index) in filterSearchQueryMobile"
                                            v-if="activeTabIndex === productGroupsSelection.length - 1"
                                            :key="index"
                                            :class="[index % 2 === 0 ? 'bg-dashboard-primary' : '']"
                                            class="duration-300 transition rounded-xl shadow-md hover:shadow-md"
                                            @click="
                                                row.selected = !row.selected;
                                                updateAllSelected();
                                                updatePieChart();
                                            "
                                        >
                                            <td class="py-4 pl-4 rounded-l-2xl">
                                                <div class="flex flex-row gap-2 items-center">
                                                    <div class="flex-shrink-0">
                                                        <div v-if="row.market_symbol_logo" class="flex-shrink-0">
                                                            <img class="w-10 h-10 rounded-full object-fit" :src="row.market_symbol_logo" />
                                                        </div>
                                                        <div
                                                            v-else
                                                            :style="{
                                                                backgroundColor: row.color
                                                            }"
                                                            class="flex items-center justify-center rounded-full w-10 h-10"
                                                        >
                                                            <i class="fi fi-rr-apartment text-white text-base"></i>
                                                        </div>
                                                    </div>
                                                    <div class="flex flex-col">
                                                        <span class="p-1 align-bottom pb-0 font-semibold" style="line-height: 1.2">
                                                            {{ row.market_symbol_name }}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td class="text-center">{{ row.amount }}</td>

                                            <td class="text-dashboard-primary-grey">
                                                <div class="flex items-center text-dashboard-primary-grey justify-center">
                                                    <span class="flex flex-col items-center">
                                                        <span class="flex items-center gap-1 font-bold">
                                                            <div
                                                                :class="getDirectionBackground(row?.direction)"
                                                                class="uppercase px-3 rounded-lg text-xxs font-semibold mr-2 mb-1 flex items-center justify-center"
                                                            >
                                                                {{ row?.direction }}
                                                            </div>
                                                            <i class="fi fi-br-enter"></i>
                                                            <span class="ml-1">{{ row.currency }} {{ row.entry }}</span>
                                                        </span>
                                                        <span class="flex items-center gap-1">
                                                            <i class="fi fi-rr-clock font-bold"></i>
                                                            <span class="ml-1">{{ row.entry_date }}, {{ row.entry_time }}</span>
                                                        </span>
                                                    </span>
                                                </div>
                                            </td>
                                            <td class="text-center">{{ row.currency }} {{ row.stop }}</td>
                                            <td v-if="filterTrades === 'finished' || filterTrades === 'all'" class="text-dashboard-primary-grey py-2">
                                                <div class="flex items-center justify-center">
                                                    <span class="flex flex-col items-center">
                                                        <span class="flex items-center gap-1 font-bold">
                                                            <i v-if="row.exit !== '-'" class="fi fi-br-exit"></i>
                                                            <span class="ml-1">{{ row.currency }} {{ row.exit }}</span>
                                                        </span>
                                                        <span v-if="row.exit !== '-'" class="flex items-center gap-1">
                                                            <span class="ml-1">({{ row.exit_date }}, {{ row.exit_time }})</span>
                                                        </span>
                                                    </span>
                                                </div>
                                            </td>
                                            <td class="text-center">{{ row.currency }} {{ row.current }}</td>
                                            <td class="text-center">
                                                <div class="flex items-center justify-center h-full w-full">
                                                    <span
                                                        :class="[
                                                            row.performance < 0
                                                                ? 'bg-dashboard-lightRed text-dashboard-lightRed'
                                                                : 'bg-dashboard-lightGreen text-dashboard-lightGreen',
                                                            'py-1 px-3 rounded-lg text-xxs font-semibold'
                                                        ]"
                                                    >
                                                        {{ formatPerformance(row.performance) }}
                                                    </span>
                                                </div>
                                            </td>
                                            <td class="text-center">
                                                <input
                                                    type="checkbox"
                                                    class="form-checkbox text-blue-500"
                                                    :checked="row.selected"
                                                    @click.stop="
                                                        row.selected = !row.selected;
                                                        updateAllSelected();
                                                        updatePieChart();
                                                    "
                                                />
                                            </td>
                                            <td
                                                class="text-center px-2 duration-300"
                                                :class="
                                                    row.expanded
                                                        ? 'rounded-tr-2xl  bg-primary-blue text-white '
                                                        : 'rounded-r-2xl text-black hover:opacity-100 bg-blue-light opacity-60'
                                                "
                                            >
                                                <router-link :to="createLink(row)" class="p-2 rounded-full h-10 w-10">
                                                    <i class="fi fi-rs-arrow-right text-lg" :class="row.expanded ? ' text-white ' : ' text-dashboard-primary-blue '"></i>
                                                </router-link>
                                            </td>
                                        </tr>

                                        <!-- Aktienpakete (grouped Trades) -->
                                        <template v-for="(group, groupIndex) in filterSearchQuery" v-else v-if="dataLoaded" :key="groupIndex">
                                            <!-- Header Row -->
                                            <tr
                                                v-show="group.trades.some((row) => filterDirectionTrades === 'Long/Short' || row.direction === filterDirectionTrades)"
                                                ref="tradesGroup"
                                                :class="{
                                                    'bg-dashboard-primary-blue text-white': group.header.expanded,
                                                    'bg-dashboard-primary': !group.header.expanded && groupIndex % 2 === 0,
                                                    'bg-white': !group.header.expanded && groupIndex % 2 !== 0,
                                                    'hover:scale-y-105  duration-300 transition': group.header.isHovered === true
                                                }"
                                                class="shadow-md rounded-xl cursor-pointer"
                                                @click="expandGroup(group)"
                                                @mouseenter="group.header.isHovered = true"
                                                @mouseleave="group.header.isHovered = false"
                                            >
                                                <td class="py-4 pl-4" :class="group.header.expanded ? 'rounded-tl-2xl' : 'rounded-l-2xl'">
                                                    <div class="flex flex-row gap-2 items-center">
                                                        <div class="flex-shrink-0">
                                                            <div v-if="group.header.market_symbol_logo" class="flex-shrink-0">
                                                                <img class="w-10 h-10 rounded-full object-fit" :src="group.header.market_symbol_logo" />
                                                            </div>
                                                            <div
                                                                v-else
                                                                :style="{
                                                                    backgroundColor: group.header.color
                                                                }"
                                                                class="flex items-center justify-center rounded-full w-10 h-10"
                                                            >
                                                                <i class="fi fi-rr-apartment text-white text-base"></i>
                                                            </div>
                                                        </div>
                                                        <div class="flex flex-col">
                                                            <span class="p-1 align-bottom pb-0 font-semibold" style="line-height: 1.2">
                                                                {{ group.header.market_symbol_name }}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="text-center font-bold">
                                                    {{ group.header.amount }}
                                                </td>
                                                <td class="text-center font-bold">
                                                    <div class="flex flex-col gap-1 items-center">
                                                        <div v-if="group.header.longEntry" class="flex items-center gap-2 w-full justify-center">
                                                            <span
                                                                :class="getDirectionBackground('Long')"
                                                                class="uppercase w-16 shrink-0 px-0 rounded-lg text-xxs font-semibold flex items-center justify-center"
                                                            >
                                                                Long
                                                            </span>
                                                            <span class="text-dashboard-dark text-left"> Ø {{ group.header.currency }} {{ group.header.longEntry }} </span>
                                                        </div>

                                                        <div v-if="group.header.shortEntry" class="flex items-center gap-2 w-full justify-center">
                                                            <span
                                                                :class="getDirectionBackground('Short')"
                                                                class="uppercase w-16 shrink-0 px-0 rounded-lg text-xxs font-semibold flex items-center justify-center"
                                                            >
                                                                Short
                                                            </span>
                                                            <span class="text-dashboard-dark text-left"> Ø {{ group.header.currency }} {{ group.header.shortEntry }} </span>
                                                        </div>
                                                    </div>
                                                </td>

                                                <td></td>
                                                <td v-if="filterTrades === 'finished' || filterTrades === 'all'" class="text-center"></td>
                                                <td class="text-center font-bold">{{ group.header.currency }} {{ group.header.current }}</td>
                                                <td class="text-center">
                                                    <div class="flex items-center justify-center h-full w-full">
                                                        <span
                                                            v-if="group.trades.some((trade) => trade.selected)"
                                                            :class="[
                                                                group.header.performance < 0
                                                                    ? 'bg-dashboard-lightRed text-dashboard-lightRed'
                                                                    : 'bg-dashboard-lightGreen text-dashboard-lightGreen',
                                                                'py-1 px-3 rounded-lg text-xxs font-semibold'
                                                            ]"
                                                        >
                                                            {{ group.header.performance ? 'Ø ' + formatPerformance(group.header.performance) : '' }}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td class="text-center">
                                                    <input
                                                        type="checkbox"
                                                        class="form-checkbox text-blue-500"
                                                        :checked="group.header.selected"
                                                        @click.stop="
                                                            group.header.selected = !group.header.selected;
                                                            toggleGroup(group.header.market_symbol_id);
                                                            updatePieChart();
                                                        "
                                                    />
                                                </td>
                                                <td
                                                    class="text-center px-2 duration-300 transition"
                                                    :class="
                                                        group.header.expanded
                                                            ? 'rounded-tr-2xl  bg-primary-blue text-white '
                                                            : 'rounded-r-2xl text-black hover:opacity-100 bg-blue-light opacity-60'
                                                    "
                                                >
                                                    <router-link :to="group.header?.link ?? '#'" class="p-2 rounded-full h-10 w-10">
                                                        <i
                                                            class="fi fi-rs-arrow-right text-lg"
                                                            :class="group.header.expanded ? ' text-white ' : ' text-dashboard-primary-blue '"
                                                        ></i>
                                                    </router-link>
                                                </td>
                                            </tr>

                                            <!-- Trades Rows -->
                                            <tr
                                                v-for="(row, rowIndex) in group.trades"
                                                v-show="group.header.expanded && (filterDirectionTrades === 'Long/Short' || filterDirectionTrades === row.direction)"
                                                :key="`${groupIndex}-${rowIndex}`"
                                                :class="[
                                                    rowIndex % 2 === 0 ? 'bg-slate-200' : 'bg-slate-50',
                                                    group.header.isHovered && 'scale-y-105 overflow-visible relative transition duration-300 shadow-md '
                                                ]"
                                            >
                                                <td
                                                    class="py-4 pl-4"
                                                    :class="{
                                                        'rounded-bl-2xl': rowIndex === group.trades.length - 1
                                                    }"
                                                    :style="{
                                                        boxShadow: 'inset 4px 0 0 0 ' + (row?.direction === 'Long' ? '#22c55e' : '#ef4444')
                                                    }"
                                                >
                                                    <div class="flex flex-row gap-2 items-center">
                                                        <div class="flex-shrink-0"></div>

                                                        <div class="flex flex-col">
                                                            <span class="p-1 align-bottom pb-0 font-semibold text-black" style="line-height: 1.2"> </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="text-center text-dashboard-primary-grey font-bold">{{ row.amount }}</td>
                                                <td class="py-2">
                                                    <div class="flex items-center text-dashboard-primary-grey justify-center">
                                                        <span class="flex flex-col items-center">
                                                            <span class="flex items-center gap-1 font-bold">
                                                                <div
                                                                    :class="getDirectionBackground(row?.direction)"
                                                                    class="uppercase px-3 rounded-lg text-xxs font-semibold mr-2 mb-1 flex items-center justify-center"
                                                                >
                                                                    {{ row?.direction }}
                                                                </div>
                                                                <i class="fi fi-br-enter"></i>
                                                                <span class="ml-1"> {{ row.currency }} {{ row.entry }}</span>
                                                            </span>
                                                            <span class="flex items-center gap-1">
                                                                <i class="fi fi-rr-clock font-bold"></i>
                                                                <span class="ml-1">{{ row.entry_date }}, {{ row.entry_time }}</span>
                                                            </span>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td class="text-center text-dashboard-primary-grey font-bold">{{ row.currency }} {{ row.stop }}</td>
                                                <td v-if="filterTrades === 'finished' || filterTrades === 'all'" class="text-dashboard-primary-grey py-2">
                                                    <div class="flex items-center justify-center">
                                                        <span class="flex flex-col items-center">
                                                            <span class="flex items-center gap-1 font-bold">
                                                                <i v-if="row.exit !== '-'" class="fi fi-br-exit"></i>
                                                                <span class="ml-1">{{ row.currency }} {{ row.exit }}</span>
                                                            </span>
                                                            <span v-if="row.exit !== '-'" class="flex items-center gap-1">
                                                                <i class="fi fi-rr-clock"></i>
                                                                <span class="ml-1">{{ row.exit_date }}, {{ row.exit_time }}</span>
                                                            </span>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td class="text-center text-dashboard-primary-grey font-bold">{{ row.currency }} {{ row.current }}</td>
                                                <td class="text-center">
                                                    <div class="flex items-center justify-center h-full w-full">
                                                        <span
                                                            :class="[
                                                                row.performance < 0
                                                                    ? 'bg-dashboard-lightRed text-dashboard-lightRed'
                                                                    : 'bg-dashboard-lightGreen text-dashboard-lightGreen',
                                                                'py-1 px-3 rounded-lg text-xxs font-semibold'
                                                            ]"
                                                        >
                                                            {{ formatPerformance(row.performance) }}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td class="text-center px-2">
                                                    <input
                                                        type="checkbox"
                                                        class="form-checkbox text-blue-500"
                                                        :checked="row.selected"
                                                        @click.stop="
                                                            row.selected = !row.selected;
                                                            setGroupSelection(group.header.market_symbol_id);
                                                            updatePieChart();
                                                        "
                                                    />
                                                </td>
                                                <td :class="rowIndex === group.trades.length - 1 ? 'rounded-br-2xl' : ''"></td>
                                            </tr>
                                        </template>
                                    </tbody>
                                </table>
                                <!--Mobile Screensize-->
                                <div class="flex flex-col gap-4">
                                    <div
                                        v-for="(row, index) in filterSearchQueryMobile"
                                        v-show="filterDirectionTrades === 'Long/Short' || filterDirectionTrades === row.direction"
                                        ref="tradesGroupMobile"
                                        :key="index"
                                        class="xl:hidden"
                                    >
                                        <PortfolioItemCard :row="row" />
                                    </div>
                                </div>
                                <no-item-found
                                    v-if="filterSearchQuery.length === 0 && loading === false"
                                    class="h-[400px] xl:block hidden"
                                    :item-name="$t('plain_trades')"
                                ></no-item-found>
                                <no-item-found v-if="filterSearchQueryMobile.length === 0 && loading === false" class="h-[400px]" :item-name="$t('plain_trades')"></no-item-found>
                            </Loading>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="h-screen">
            <no-item-found :item-name="$t('plain_trades')"></no-item-found>
        </div>
    </div>
</template>

<script>
import '@bhplugin/vue3-datatable/dist/style.css';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import Highcharts from 'highcharts';
import i18next from 'i18next';
import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import NoItemFound from '../components/dashboard/noItemFound.vue';
import MetricsCards from '../components/dashboard/portfolio/MetricsCards.vue';
import TutorialBanner from '../components/TutorialBanner.vue';
import { fetchData } from '../fetchData.js';
import { formatDateByRegion, generateRandomBluePurpleTone, lightenColor } from '../helpers.js';
import PortfolioItemCard from './../components/PortfolioItemCard.vue';

export default {
    components: {
        TutorialBanner,
        MetricsCards,
        NoItemFound,
        PortfolioItemCard,

        ...mapActions('contentstream', ['recentLinks']),
        ...mapGetters('user', {
            user: 'get'
        }),
        ...mapGetters('trades', {
            get: 'get'
        }),
        ...mapActions('performance', ['getByTrades'])
    },
    data: () => ({
        showShortTutorialMessage: new Date() < new Date('2025-10-01'),

        loading: true,
        chartloading: true,
        teaserActive: false,

        userId: null,
        globalSearch: null,

        filterTrades: 'active',
        tradeFilterOptions: [
            { value: 'all', label: 'dashboard_portfolio_filter_states_all' },
            { value: 'active', label: 'dashboard_portfolio_filter_states_active' },
            { value: 'finished', label: 'dashboard_portfolio_filter_states_finished' }
        ],
        filterDirectionTrades: 'Long/Short',
        filterDirection: ['Long/Short', 'Long', 'Short'],
        // Drop Down & Timeline Filter
        chartTimelineStates: [
            { title: 'portfolio_chart_timeline_filter.max.title', acronym: 'portfolio_chart_timeline_filter.max.acronym' },
            { title: 'portfolio_chart_timeline_filter.5J.title', acronym: 'portfolio_chart_timeline_filter.5J.acronym' },
            { title: 'portfolio_chart_timeline_filter.2J.title', acronym: 'portfolio_chart_timeline_filter.2J.acronym' },
            { title: 'portfolio_chart_timeline_filter.1J.title', acronym: 'portfolio_chart_timeline_filter.1J.acronym' },
            { title: 'portfolio_chart_timeline_filter.6M.title', acronym: 'portfolio_chart_timeline_filter.6M.acronym' }
        ],
        timelineIndex: 4,
        // search Trades by name
        searchQuery: null,
        // Distinction between 'Latest Trades' without grouping and grouped trades by MarketSymbol
        // sorted by entry_date
        newTrades: [],
        // new Trades alphabetical sorted
        sortedTrades: [],
        // grouped Trades by MarketSymbol
        groupedTrades: [],
        filteredAndSearchedNewTrades: [],
        filteredandSearchedGroupedTrades: [],
        // To track indices of selected rows (for performance)
        selectedTrades: [],
        selectedHeader: [],
        areAllTradesSelected: true,
        // Product Groups to filter table
        productGroupsSelection: [{ id: null, name: 'Neueste Investments' }],
        activeTabIndex: 0,
        dataLoaded: false, // Flag to track data loading
        stockPerformanceData: [], // Array for raw performance data
        performanceData: [], // Array for calculated performance data
        pieChartData: null,
        tradesFound: true
    }),
    computed: {
        pieChartTitles() {
            return {
                title: i18next.t('selected_stocks'),
                subtitle: i18next.t('allocation'),
                seriesTitle: i18next.t('allocation_alt')
            };
        },

        cardObjects() {
            return [
                {
                    icon: 'fi-rr-bullseye',
                    number: this.formatProfit(this.getProfit('active')),
                    title: 'portfolio_profit',
                    description: 'portfolio_average_active_trades'
                },
                {
                    icon: this.getBestPerformer().market_symbol_logo,
                    number: this.formatProfit(Math.round(this.getBestPerformer().performance)),
                    title: 'portfolio_best_performer',
                    description: 'portfolio_trade_best_performance',
                    symboleName: this.getBestPerformer().market_symbol_name
                },
                {
                    icon: 'fi-rr-coin-up-arrow ',
                    number: this.formatProfit(this.getProfit('finished')),
                    title: 'portfolio_realized_profit',
                    description: 'portfolio_average_closed_trades'
                }
            ];
        },

        // returns the trades filtered by trade state only
        filterQuery() {
            return this.getFilteredTrades(false);
        },

        filterQueryOnlyGrouped() {
            return this.getFilteredTrades(false, true);
        },

        // applies both search and trade state filters
        filterSearchQuery() {
            return this.getFilteredTrades(true);
        },

        filterSearchQueryMobile() {
            return this.getFilteredTrades(true, false);
        },
        filterQueryMobile() {
            return this.getFilteredTrades(false, false);
        }
    },
    watch: {
        activeTabIndex() {
            if (this.activeTabIndex === this.productGroupsSelection.length - 1) {
                this.filterDirectionTrades = 'Long/Short';
            }
        }
    },

    mounted() {
        this.init();
    },

    methods: {
        startHelpTour() {
            const isMobile = window.innerWidth < 1280;
            this.activeTabIndex = 0;
            const steps = [
                {
                    popover: {
                        title: i18next.t('helpTour_portfolio_step1.title'),
                        description: i18next.t('helpTour_portfolio_step1.description'),
                        side: 'bottom',
                        align: 'start'
                    }
                },
                {
                    element: this.$refs.tradeTable,
                    popover: {
                        title: i18next.t('helpTour_portfolio_step2.title'),
                        description: i18next.t('helpTour_portfolio_step2.description'),
                        side: 'top',
                        align: 'start'
                    }
                },
                {
                    element: isMobile ? this.$refs.tradesGroupMobile?.[0] : this.$refs.tradesGroup?.[0],
                    popover: {
                        title: i18next.t('helpTour_portfolio_step3.title'),
                        description: i18next.t('helpTour_portfolio_step3.description'),
                        side: 'top',
                        align: 'start'
                    }
                },
                {
                    element: this.$refs.tradeTable,
                    popover: {
                        title: i18next.t('helpTour_portfolio_step4.title'),
                        description: i18next.t('helpTour_portfolio_step4.description'),
                        side: 'top',
                        align: 'start'
                    },
                    onHighlightStarted: () => {
                        if (this.filterSearchQuery?.length > 0) {
                            this.filterSearchQuery[0].header.expanded = true;
                        }
                    }
                },
                {
                    element: this.$refs.longShortFilter,
                    popover: {
                        title: i18next.t('helpTour_portfolio_step5.title'),
                        description: i18next.t('helpTour_portfolio_step5.description'),
                        side: 'bottom',
                        align: 'start'
                    },
                    onHighlightStarted: () => {
                        if (this.filterSearchQuery?.length > 0) {
                            this.filterSearchQuery[0].header.expanded = false;
                        }
                    }
                },
                {
                    element: this.$refs.tradeFilterChartOpenClosed,
                    popover: {
                        title: i18next.t('helpTour_portfolio_step6.title'),
                        description: i18next.t('helpTour_portfolio_step6.description'),
                        side: 'top',
                        align: 'start'
                    }
                },
                {
                    element: this.$refs.tradeFilterOpenClosed,
                    popover: {
                        title: i18next.t('helpTour_portfolio_step7.title'),
                        description: i18next.t('helpTour_portfolio_step7.description'),
                        side: 'top',
                        align: 'start'
                    }
                },
                {
                    popover: {
                        title: i18next.t('helpTour_portfolio_step8.title'),
                        description: i18next.t('helpTour_portfolio_step8.description'),
                        side: 'top',
                        align: 'start'
                    }
                }
            ];

            const driverObj = driver({
                allowHtml: true,
                showProgress: true,
                nextBtnText: i18next.t('plain_next'),
                prevBtnText: i18next.t('plain_back'),
                doneBtnText: i18next.t('plain_done'),
                steps
            });

            driverObj.drive();
        },
        async init() {
            await this.loadProductGroups();
            this.updatePieChart();
        },
        createLink(target) {
            const link = target?.link;

            if (link) {
                return link;
            }

            return '#';
        },

        getDirectionBackground(direction) {
            const background = {
                Long: 'bg-dashboard-lightGreen text-dashboard-lightGreen',
                Short: 'bg-dashboard-lightRed text-dashboard-lightRed'
            };

            return background[direction] || '';
        },

        getFilteredTrades(searchEnabled = true, grouped = null) {
            let filteredNewTrades = [];

            if (this.activeTabIndex !== this.productGroupsSelection.length - 1) {
                filteredNewTrades = [...this.sortTradesByName(this.newTrades)];
            } else {
                filteredNewTrades = [...this.newTrades].sort((a, b) => new Date(b.update_date) - new Date(a.update_date)).slice(0, 50);
            }

            let filteredGroupedTrades = this.groupedTrades.map((tradeGroup) => ({
                ...tradeGroup,
                trades: [...tradeGroup.trades]
            }));

            // Apply search filter if enabled and there's a search query
            if (searchEnabled && this.searchQuery) {
                const queryWords = this.searchQuery.toLowerCase().split(' ');

                const filterBySearchQuery = (trade) => queryWords.every((word) => trade.market_symbol_name.toLowerCase().includes(word));

                if (grouped === false) {
                    filteredNewTrades = filteredNewTrades.filter(filterBySearchQuery);
                } else if (this.activeTabIndex !== this.productGroupsSelection.length - 1 || grouped) {
                    filteredGroupedTrades = filteredGroupedTrades
                        .map((tradeGroup) => ({
                            ...tradeGroup,
                            trades: tradeGroup.trades.filter(filterBySearchQuery)
                        }))
                        .filter((tradeGroup) => tradeGroup.trades.length > 0);
                }
            }

            // Apply trade state filter
            const filterByTradeState = (trade) => (this.filterTrades === 'active' ? trade.exit === '-' : trade.exit !== '-');

            if (this.filterTrades === 'active' || this.filterTrades === 'finished') {
                if (grouped === false) {
                    filteredNewTrades = filteredNewTrades.filter(filterByTradeState);
                } else if (this.activeTabIndex !== this.productGroupsSelection.length - 1 || grouped) {
                    filteredGroupedTrades = filteredGroupedTrades
                        .map((tradeGroup) => ({
                            ...tradeGroup,
                            trades: tradeGroup.trades.filter(filterByTradeState)
                        }))
                        .filter((tradeGroup) => tradeGroup.trades.length > 0);
                    this.calculateHeaderPerformanceExitAmount(filteredGroupedTrades);
                }
            }

            if (grouped === true) {
                return filteredGroupedTrades;
            }

            return this.activeTabIndex !== this.productGroupsSelection.length - 1 && grouped !== false ? filteredGroupedTrades : filteredNewTrades;
        },

        expandGroup(group) {
            group.header.expanded = !group.header.expanded;
        },

        getBestPerformer() {
            // Determine whether to get grouped trades or new trades based on the active tab index
            const filteredTrades = this.activeTabIndex !== this.productGroupsSelection.length - 1 ? this.groupedTrades : this.newTrades;

            // Initialize variables to track the best performance
            let bestPerformance = -Infinity;
            let bestTrade = null;

            for (const group of filteredTrades) {
                // Handle the case where trades are grouped (group.trades) or directly available (new trades)
                const tradeList = group.trades || filteredTrades;

                for (const trade of tradeList) {
                    const performance = parseFloat(trade.performance);

                    // Validate the performance value
                    if (!isNaN(performance)) {
                        // Check if this performance is the best so far
                        if (performance > bestPerformance) {
                            bestPerformance = performance;
                            bestTrade = trade;
                        }
                    }
                }
            }

            // Return the trade with the best performance or a message if no valid performance found
            return bestTrade || { market_symbol_name: '...' };
        },

        getProfit(tradeFilterState) {
            let totalPerformance = 0;
            let totalAmount = 0;

            let trades = _.cloneDeep(this.activeTabIndex !== this.productGroupsSelection.length - 1 ? this.groupedTrades : this.newTrades);

            const filterTrades = (trade) => (tradeFilterState === 'active' ? trade.exit === '-' : trade.exit !== '-');

            if (this.activeTabIndex !== this.productGroupsSelection.length - 1) {
                trades = trades
                    .map((group) => ({
                        ...group,
                        trades: group.trades.filter(filterTrades)
                    }))
                    .filter((group) => group.trades.length > 0);
            } else {
                trades = trades.filter(filterTrades);
            }

            trades.forEach((group) => {
                (group.trades || [group]).forEach((trade) => {
                    if (!isNaN(trade.amount) && !isNaN(trade.performance)) {
                        totalPerformance += trade.performance * trade.amount;
                        totalAmount += trade.amount;
                    }
                });
            });

            return totalAmount ? totalPerformance / totalAmount : 0;
        },

        // Productgroup/View-Selection
        async changeViewSelection(index) {
            this.loading = true;
            this.activeTabIndex = index;
            await this.updateTable();
            this.clearExpandedStates();

            this.loading = false;
        },

        clearExpandedStates() {
            this.groupedTrades = this.groupedTrades.map((group) => {
                group.header.expanded = false;

                return group;
            });
        },

        // Table Data Update
        async loadProductGroups() {
            this.loading = true;
            try {
                const { result = [] } = await fetchData('/api/v1/trades?sort=&subscribed_abos=true&tradeType=buy_and_hold');
                const trades = result.data;

                if (trades.length === 0) {
                    this.tradesFound = false;
                }

                trades.forEach((trade) => {
                    trade.market_symbol.productGroups?.forEach((groups) => {
                        groups.forEach((item) => {
                            if (!this.productGroupsSelection.some((group) => group.id === item.id)) {
                                this.productGroupsSelection.unshift({
                                    id: item.id,
                                    name: item.name
                                });
                            }
                        });
                    });
                });
                const mining = { de: 'Minen', en: 'Mining' };

                this.productGroupsSelection.sort((a, b) => {
                    if (a.name === 'Neueste Investments') return 1;

                    if (b.name === 'Neueste Investments') return -1;

                    if (a.name.de === mining.de || a.name.en === mining.en) return 1;

                    if (b.name.de === mining.de || b.name.en === mining.en) return -1;

                    return 0;
                });
                this.productGroupsLoaded = true;
                await this.updateTable();
                this.loading = false;
            } catch (error) {
                console.error('Error loading product groups:', error);
                this.loading = false;
            }
        },

        async updateTable() {
            if (!this.productGroupsLoaded) {
                console.warn('Product groups are not fully loaded.');

                return;
            }

            this.newTrades = [];
            this.dataLoaded = false;

            try {
                await this.fetchUserId();

                if (this.isActiveTabIndexOutOfBounds()) return;

                const tradesResponse = await this.fetchTradesResponse();
                await this.loadAdditionalProductGroups();

                this.processTradesResponse(tradesResponse);
                this.dataLoaded = true;
                this.loading = false;
            } catch (error) {
                console.error('Error loading trades:', error);
                this.dataLoaded = true;
                this.loading = false;
            }
        },
        async fetchUserId() {
            const response = await this.$store.dispatch('user/user');
            this.userId = response.data?.id;
        },

        isActiveTabIndexOutOfBounds() {
            if (this.activeTabIndex < 0 || this.activeTabIndex >= this.productGroupsSelection.length) {
                console.warn('Active index is out of bounds.');
                this.dataLoaded = true;

                return true;
            }

            return false;
        },

        async fetchTradesResponse() {
            /*     const config = {
                params: {
                    subscribed_abos: true,
                    selectedProduct: [this.productGroupsSelection[this.activeTabIndex].id],
                    key: this.productGroupsSelection[this.activeTabIndex].id ?? 'dashboard_trades_tab',
                    sort: '',
                    ...(this.globalSearch && { search: this.globalSearch })
                }
            };

            let tradesResponse = this.productGroupsSelection[this.activeTabIndex].id
                ? this.$store.getters['settings/isContentLoaded'](this.productGroupsSelection[this.activeTabIndex].id)
                    ? this.$store.getters['trades/getByKey'](this.productGroupsSelection[this.activeTabIndex].id)
                    : await this.$store.dispatch('trades/index', config)
                : this.$store.getters['trades/get'].data;

            if (!tradesResponse) {
                tradesResponse = await this.$store.dispatch('trades/index', config);
            } */

            // 1. Basis-URL und Parameter-Objekt erstellen
            const baseUrl = '/api/v1/trades';
            const params = new URLSearchParams({
                subscribed_abos: 'true',
                sort: '',
                tradeType: 'buy_and_hold'
            });

            // 2. Produkt-ID hinzufügen, wenn ein spezifischer Tab ausgewählt ist
            const selectedProductGroup = this.productGroupsSelection[this.activeTabIndex];

            if (selectedProductGroup && selectedProductGroup.id) {
                params.append('selectedProduct[]', selectedProductGroup.id);
            }

            // 3. Globale Suche hinzufügen, falls vorhanden
            if (this.globalSearch) {
                params.append('search', this.globalSearch);
            }

            // 4. Den Request mit der zusammengesetzten URL ausführen
            const { result = [] } = await fetchData(`${baseUrl}?${params.toString()}`);
            this.$store.commit('settings/contentLoaded', this.productGroupsSelection[this.activeTabIndex].id);

            return result;
        },

        async loadAdditionalProductGroups() {
            this.productGroupsSelection.forEach((item, index) => {
                if (index !== this.activeTabIndex && !this.$store.getters['settings/isContentLoaded'](item.id)) {
                    this.$store
                        .dispatch('trades/index', {
                            params: {
                                subscribed_abos: true,
                                selectedProduct: [item.id],
                                key: item.id ?? 'dashboard_trades_tab',
                                sort: ''
                            }
                        })
                        .then(() => {
                            this.$store.commit('settings/contentLoaded', item.id);
                        });
                }
            });
        },

        processTradesResponse(tradesResponse) {
            let index = 0;
            const market_symbol_names = [];

            for (const trade of tradesResponse.data || []) {
                const color = trade.market_symbol.brand_data
                    ? lightenColor(JSON.parse(trade.market_symbol.brand_data).colors[0].hex, 0.3)
                    : generateRandomBluePurpleTone(index, tradesResponse.data.length);

                this.newTrades.push({
                    id: trade.id,
                    currency: trade.market_symbol.currency,
                    market_symbol_id: trade.market_symbol_id,
                    number: index,
                    amount: trade.amount,
                    market_symbol_name: trade.market_symbol.name,
                    market_symbol_logo: trade.market_symbol.logo,
                    direction: trade.direction,
                    entry: trade.entry_price ? trade.entry_price : '-',
                    entry_date: formatDateByRegion(trade.entry_time ? this.$moment(trade.entry_time) : null),
                    entry_time: trade.entry_time
                        ? new Intl.DateTimeFormat(navigator.language, {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: undefined // Automatic adjustment to the user's system (24h or 12h -> AM/PM)
                          }).format(new Date(trade.entry_time))
                        : null,
                    entry_date_time: trade.entry_time || null,
                    exit: trade.exit_price
                        ? trade.exit_price
                              .toString()
                              .replace('.', ',')
                              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                        : '-',
                    exit_date: formatDateByRegion(trade.exit_time ? this.$moment(trade.exit_time) : null),
                    exit_time: trade.exit_time
                        ? new Intl.DateTimeFormat(navigator.language, {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: undefined // Automatic adjustment to the user's system (24h or 12h -> AM/PM)
                          }).format(new Date(trade.exit_time))
                        : null,
                    exit_date_time: trade.exit_time || null,
                    stop: trade.stop_loss
                        ? trade.stop_loss
                              .toString()
                              .replace('.', ',')
                              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                        : '-',
                    updated_at: trade.updated_at || trade.created_at,
                    current: trade.market_symbol.last_price
                        ? trade.market_symbol.last_price
                              .toFixed(2)
                              .replace('.', ',')
                              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                        : '-',
                    performance: this.calculatePerformance(trade),
                    selected: true,
                    color,
                    brand_data: trade.market_symbol.brand_data,
                    link: `/dashboard/markets-analysis/${trade?.recent_contentstream}`
                });

                if (!market_symbol_names.includes(trade.market_symbol.name)) {
                    market_symbol_names.push(trade.market_symbol.name);
                }

                index++;
            }

            this.groupTrades();
            this.updatePieChart();
        },

        sortTradesByName(trades) {
            return [...trades].sort((a, b) => a.market_symbol_name.localeCompare(b.market_symbol_name));
        },

        groupTrades() {
            this.groupedTrades = [];
            this.sortedTrades = this.sortTradesByName(this.newTrades);
            let currentMarketSymbol = null;

            for (const trade of this.sortedTrades) {
                if (trade.market_symbol_id !== currentMarketSymbol) {
                    currentMarketSymbol = trade.market_symbol_id;

                    this.groupedTrades.push({
                        header: {
                            currency: trade.currency,
                            market_symbol_name: trade.market_symbol_name,
                            market_symbol_id: trade.market_symbol_id,
                            market_symbol_logo: trade.market_symbol_logo,
                            amount: null,
                            performance: null,
                            current: trade.current,
                            entry: null,
                            exit: null,
                            exit_date: null,
                            exit_time: null,
                            selected: true,
                            expanded: false,
                            hovered: false,
                            color: trade.color,
                            brand_data: trade.brand_data,
                            link: trade.link
                        },
                        trades: []
                    });
                }

                this.groupedTrades[this.groupedTrades.length - 1].trades.push(trade);
            }

            this.groupedTrades.forEach((group) => {
                group.trades.sort((a, b) => {
                    const [dA, mA, yA] = a.entry_date.split('.').map(Number);
                    const [dB, mB, yB] = b.entry_date.split('.').map(Number);
                    const dateA = new Date(yA, mA - 1, dA);
                    const dateB = new Date(yB, mB - 1, dB);

                    return dateB - dateA;
                });
            });
            this.groupedTrades = this.calculateHeaderPerformanceExitAmount(this.groupedTrades);
        },

        calculateHeaderPerformanceExitAmount(groupedTrades) {
            const amountFactor = 5000; // fixed investment per trade for share calculation

            groupedTrades.forEach((group) => {
                let totalLongAmount = 0;
                let totalShortAmount = 0;
                let totalPerformance = 0;
                let totalAmount = 0;
                let totalLongShares = 0;
                let totalShortShares = 0;
                let totalLongInvested = 0;
                let totalShortInvested = 0;

                group.trades.forEach((trade) => {
                    const amount = trade.amount;
                    const entryPrice = parseFloat(trade.entry);
                    const performance = parseFloat(trade.performance);

                    if (!isNaN(amount) && !isNaN(entryPrice) && !isNaN(performance)) {
                        // overall performance
                        totalPerformance += performance * amount;
                        totalAmount += amount;

                        // share-based weighting for entries
                        const shares = (amount * amountFactor) / entryPrice;

                        if (trade.direction === 'Long') {
                            totalLongAmount += amount;
                            totalLongShares += shares;
                            totalLongInvested += shares * entryPrice;
                        } else if (trade.direction === 'Short') {
                            totalShortAmount += amount;
                            totalShortShares += shares;
                            totalShortInvested += shares * entryPrice;
                        }
                    }
                });

                // invested/amount-weighted performance
                group.header.performance = totalAmount > 0 ? (totalPerformance / totalAmount).toFixed(2) : null;

                // weighted entry by shares: invested / shares
                group.header.longEntry = totalLongShares > 0 ? (totalLongInvested / totalLongShares).toFixed(2) : null;

                group.header.shortEntry = totalShortShares > 0 ? (totalShortInvested / totalShortShares).toFixed(2) : null;

                // total number of trades
                group.header.amount = totalAmount;
            });

            return groupedTrades;
        },

        // Select Boxes

        // Handle group header selection when all trades are manually selected or deselected
        setGroupSelection(market_symbol_id) {
            const group = this.groupedTrades.find((group) => group.header.market_symbol_id === market_symbol_id);

            const isWholeGroupSelected = group.trades.every((trade) => trade.selected === true);

            if (isWholeGroupSelected) {
                group.header.selected = true;
                this.updateAllSelected();
            } else {
                group.header.selected = false;
                this.areAllTradesSelected = false;
            }
        },

        // Handle group header selection
        toggleGroup(market_symbol_id) {
            const group = this.groupedTrades.find((group) => group.header.market_symbol_id === market_symbol_id);

            if (group) {
                const setTo = group.header.selected;
                group.trades.forEach((trade) => (trade.selected = setTo));
                this.areAllTradesSelected = setTo ? this.updateAllSelected() : false;
            }
        },

        setAllTradesTo(state) {
            if (this.activeTabIndex === this.productGroupsSelection.length - 1) {
                this.newTrades = this.newTrades.map((trade) => {
                    trade.selected = state; // Using the `state` parameter to set the desired state

                    return trade;
                });
            } else {
                this.groupedTrades = this.groupedTrades.map((group) => {
                    group.header.selected = state; // Set the header's selected property
                    group.trades = group.trades.map((trade) => {
                        trade.selected = state;

                        return trade;
                    });

                    return group;
                });
            }

            this.areAllTradesSelected = state;
        },

        // Update global Header if all Trades and Headers are (single or group/manually) selected
        updateAllSelected() {
            this.areAllTradesSelected =
                this.activeTabIndex === this.productGroupsSelection.length - 1
                    ? this.newTrades.every((trade) => trade.selected)
                    : this.groupedTrades.every((group) => group.header.selected);
        },

        formatPerformance(number) {
            const parsedNumber = parseFloat(number);

            if (isNaN(parsedNumber) || parsedNumber === 0) return '-';

            let performance = parsedNumber
                .toFixed(2)
                .replace('.', ',')
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.');

            if (parsedNumber > 0) performance = '+' + performance;

            return performance + '%';
        },

        formatProfit(number) {
            if (typeof number === 'number' && !isNaN(number)) {
                return Math.round(number) + '%';
            } else {
                return '0%';
            }
        },

        calculatePerformance(trade) {
            const entryPrice = trade.entry_price;
            const exitPrice = trade.exit_price || trade.market_symbol.last_price;

            if (trade.direction.toLowerCase() === 'short') {
                return entryPrice && exitPrice ? parseFloat(((entryPrice - exitPrice) / entryPrice) * 100).toFixed(2) : null;
            }

            return entryPrice && exitPrice ? parseFloat(((exitPrice - entryPrice) / entryPrice) * 100).toFixed(2) : null;
        },

        // updatePieChart function updates the pie chart and performance chart based on selected trades and timeline
        updatePieChart() {
            const now = new Date();
            const fromDate = this.getFromDate(now, this.timelineIndex);
            const from = fromDate.toISOString().split('T')[0];
            const to = now.toISOString().split('T')[0];

            // Filter grouped trades based on selection
            let filteredGroupedTrades = this.filterQueryOnlyGrouped
                .map((group) => ({
                    ...group,
                    trades: group.trades.filter((trade) => trade.selected)
                }))
                .filter((group) => group.trades.length > 0);

            if (this.filterDirectionTrades !== 'Long/Short') {
                filteredGroupedTrades = filteredGroupedTrades
                    .map((group) => ({
                        ...group,
                        trades: group.trades.filter((trade) => trade.direction === this.filterDirectionTrades)
                    }))
                    .filter((group) => group.trades.length > 0);
            }

            filteredGroupedTrades = this.calculateHeaderPerformanceExitAmount(filteredGroupedTrades);

            // Set pie chart data based on filtered trades
            if (!filteredGroupedTrades.length) {
                this.pieChartData = [
                    {
                        name: 'Keine Trades im Portfolio',
                        y: 100,
                        color: '#B8B3F9',
                        selected: true
                    }
                ];
            } else if (this.activeTabIndex !== this.productGroupsSelection.length - 1) {
                this.pieChartData = this.getPieChartData(filteredGroupedTrades);
            } else {
                this.pieChartData = this.getMobilePieChartData();
            }

            // Get selected trades IDs for performance chart
            const selectedTradesIds = this.getSelectedTradesIds(filteredGroupedTrades);

            // Fetch and render performance chart data
            if (selectedTradesIds.length) {
                this.chartloading = true;
                this.$store
                    .dispatch('performance/getByTrades', {
                        from,
                        to,
                        trades: selectedTradesIds
                    })
                    .then((performanceChartData) => {
                        this.performanceData = this.formatPerformanceData(performanceChartData.data);
                        this.renderPerformanceChart();
                        this.chartloading = false;
                    })
                    .catch(() => {
                        this.chartloading = false;
                    });
            } else {
                this.performanceData = [[Date.now(), 0]];
                this.renderPerformanceChart();
            }

            // Render pie chart
            this.renderPieChart();
        },

        getFromDate(now, timelineIndex) {
            switch (timelineIndex) {
                case 0:
                    return new Date('2000-01-01');
                case 1:
                    return new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
                case 2:
                    return new Date(now.getFullYear() - 2, now.getMonth(), now.getDate());
                case 3:
                    return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
                case 4:
                    return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                default:
                    return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
            }
        },

        getPieChartData(filteredGroupedTrades) {
            const highestAmountGroup = filteredGroupedTrades.reduce((maxGroup, group) => (group.header.amount > maxGroup.header.amount ? group : maxGroup));
            const highestAmountIndex = filteredGroupedTrades.indexOf(highestAmountGroup);
            const absoluteAmount = filteredGroupedTrades.reduce((total, group) => total + group.header.amount * (1 + (group.header.performance || 0) / 100), 0);

            return filteredGroupedTrades
                .filter((group) => group.header.performance)
                .map((group, index) => ({
                    name: group.header.market_symbol_name,
                    y: parseFloat((((group.header.amount * (1 + group.header.performance / 100)) / absoluteAmount) * 100).toFixed(2)),
                    color: group.header.color || generateRandomBluePurpleTone(index, filteredGroupedTrades.length),
                    ...(index === highestAmountIndex && {
                        sliced: true,
                        selected: true
                    })
                }));
        },

        getMobilePieChartData() {
            const groupedTrades = this.filterQueryMobile
                .slice(0, 50)
                .filter((trade) => trade.selected)
                .reduce((acc, trade) => {
                    if (!acc[trade.market_symbol_name]) {
                        acc[trade.market_symbol_name] = {
                            name: trade.market_symbol_name,
                            performance: parseFloat(trade.performance),
                            amount: 0,
                            color: trade.color || null
                        };
                    }

                    acc[trade.market_symbol_name].amount += trade.amount;

                    return acc;
                }, {});
            const totalAmount = Object.values(groupedTrades).reduce((total, trade) => total + trade.amount * (1 + trade.performance / 100), 0);

            return Object.values(groupedTrades).map((trade, index) => ({
                name: trade.name,
                y: Math.round(((trade.amount * (1 + trade.performance / 100) * 100) / totalAmount) * 100) / 100,
                color: trade.color || generateRandomBluePurpleTone(index, Object.keys(groupedTrades).length)
            }));
        },

        getSelectedTradesIds(filteredGroupedTrades) {
            return this.activeTabIndex !== this.productGroupsSelection.length - 1
                ? filteredGroupedTrades.flatMap((group) => group.trades.map((trade) => trade.id))
                : this.filterQueryMobile
                      .slice(0, 50)
                      .filter((trade) => trade.selected)
                      .map((trade) => trade.id);
        },

        formatPerformanceData(rawData) {
            return Object.entries(rawData)
                .map(([dateStr, valueStr]) => [new Date(dateStr).getTime(), parseFloat(valueStr)])
                .sort((a, b) => a[0] - b[0]);
        },

        renderPerformanceChart() {
            Highcharts.chart('graphChart', {
                chart: { type: 'area' },
                title: { text: 'Performance' },
                xAxis: { type: 'datetime', title: { text: 'Zeit' } },
                yAxis: { title: { text: 'Performance (%)' } },
                plotOptions: {
                    area: {
                        lineWidth: 1,
                        marker: { radius: 2 },
                        color: {
                            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                            stops: [
                                [0, '#45bf9b'],
                                [1, '#45bf9b']
                            ]
                        },
                        negativeColor: '#e84646'
                    }
                },
                series: [
                    {
                        data: this.performanceData.length ? this.performanceData : [[Date.now(), 0]],
                        name: 'Performance (%)'
                    }
                ]
            });
        },

        renderPieChart() {
            Highcharts.chart('pieChart', {
                chart: { type: 'pie' },
                title: { text: this.pieChartTitles.title },
                tooltip: { valueSuffix: '%' },
                subtitle: { text: this.pieChartTitles.subtitle },
                plotOptions: {
                    pie: {
                        allowPointSelect: true,
                        cursor: 'pointer',
                        dataLabels: {
                            enabled: true,
                            distance: 20,
                            formatter: function () {
                                const maxLength = 10;
                                const words = this.point.name.split(' ');
                                let formattedName = '';
                                let lineLength = 0;

                                words.forEach((word) => {
                                    if (lineLength + word.length > maxLength) {
                                        formattedName += '<br/>';
                                        lineLength = 0;
                                    }

                                    formattedName += word + ' ';
                                    lineLength += word.length + 1;
                                });

                                return formattedName.trim();
                            }
                        },
                        showInLegend: false
                    }
                },
                series: [{ name: this.pieChartTitles.seriesTitle, colorByPoint: true, data: this.pieChartData }]
            });
        }
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
